import React, { useEffect, useLayoutEffect, useState } from "react";
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import Chart from "../../../../Components/Chart/Chart";
import useChart from "../../../../Components/Chart/useChart";
import { createDateAxis, createValueAxis } from "../TrendsUtils";
import useTrend from "../useTrend";
import type { TimeScale } from "../TimeScaleSelector/TimeScaleSelector";
import type { SelectedScope } from "../../../../types/scope";

// Single point in the chart series, derived from each `[date, value]` tuple
// returned by the trend query.
interface AvgTicketAgeChartDatum {
  date: string;
  value: number;
  dashLength?: number;
}

// Raw trend rows are `[date, value]` tuples coming from parsed JSON; trendData
// from useTrend is typed `unknown`, so we narrow to this shape locally.
type TrendRow = [string, number];

export interface AvgTicketAgeProps {
  selectedScope?: SelectedScope | null;
  filterToExecute?: string | null;
  timeScale: TimeScale;
  scopeToExecute?: string | null;
  id: string | number;
  widgetKey?: string;
  setIsReadyToExport?: ((isReady: boolean) => void) | null;
}

const AvgTicketAge: React.FC<AvgTicketAgeProps> = ({
  selectedScope,
  filterToExecute,
  timeScale,
  scopeToExecute,
  id,
  widgetKey,
  setIsReadyToExport = null,
}) => {
  const {
    trendData: data,
    trendDataError,
    trendLoading,
    refetchTrend,
  } = useTrend({
    variables: {
      ...(widgetKey ? { widgetKey } : {}),
      selection: ["ticket_age"],
      timescale: timeScale,
      aggregate_functions: ["AVG"],
      round_functions: ["ROUND"],
      filters_config: {
        filtersjson: filterToExecute,
        scopesjson: scopeToExecute,
        scopesid: selectedScope?.value?.id,
        filters_fields_to_exclude: ["actual_status"],
      },
      snapshots_filters_config: {
        filtersjson: filterToExecute,
        filters_fields_to_include: ["ticket_status"],
      },
    },
    skip: (!scopeToExecute && !selectedScope?.value?.id) || !filterToExecute,
  });

  const [trendData, setTrendData] = useState<AvgTicketAgeChartDatum[] | null>(
    null
  );
  const [max, setMax] = useState(0);
  const chartName = `avgTicketAge-${id}`;

  const {
    chartLoading,
    dataError,
    dataExists,
    setChartError,
    setChartDataExists,
    startLoadingChart,
    stopLoadingChart,
    createChart,
  } = useChart(setIsReadyToExport);

  const clearData = () => {
    setTrendData(null);
  };

  useEffect(() => {
    if (!!trendLoading) {
      startLoadingChart();
      clearData();
      setChartError(false);
      setChartDataExists(false);
    }
  }, [trendLoading, setChartDataExists, setChartError, startLoadingChart]);

  useEffect(() => {
    if (!!trendDataError) {
      setChartError(true);
      stopLoadingChart();
    }
  }, [trendDataError, setChartError, stopLoadingChart]);

  useEffect(() => {
    if (!!data) {
      const rows = data as TrendRow[];
      let ticketAgeByTimeScale: AvgTicketAgeChartDatum[] = [];
      ticketAgeByTimeScale = rows.map((timeScale, index) => {
        const dataVal: AvgTicketAgeChartDatum = {
          date: timeScale[0],
          value: timeScale[1],
        };

        if (rows?.length >= 2 && index === rows?.length - 2) {
          dataVal.dashLength = 8;
        }
        return dataVal;
      });

      if (
        !ticketAgeByTimeScale?.length ||
        !ticketAgeByTimeScale?.find((item) => item.value !== 0)
      ) {
        stopLoadingChart();
        setChartDataExists(false);
      } else if (ticketAgeByTimeScale?.length) {
        setChartError(false);
        stopLoadingChart();
        setChartDataExists(true);
      }

      let maxNum = 0;
      rows.forEach((item) => {
        item?.forEach((value) => {
          if ((value as number) > maxNum) {
            maxNum = value as number;
          }
        });
      });

      setMax(maxNum * 1.2);
      setTrendData(ticketAgeByTimeScale);
    }
  }, [data]);

  useLayoutEffect(() => {
    if (dataExists) {
      let [chart, legendContainer] = createChart(chartName, am4charts.XYChart);

      // Preserve runtime behavior: original assigned `trendData` directly,
      // which may be null; amCharts tolerates it.
      chart.data = trendData as AvgTicketAgeChartDatum[];

      chart.cursor = new am4charts.XYCursor();
      chart.paddingTop = 45;
      chart.paddingBottom = -10;
      chart.paddingRight = 40;

      const dateAxis = chart.xAxes.push(new am4charts.DateAxis());
      createDateAxis(dateAxis, timeScale);
      const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      createValueAxis(valueAxis, "Ticket Age", max);

      let label = dateAxis.renderer.labels.template;
      label.wrap = true;
      label.maxWidth = 85;
      label.textAlign = "middle";

      const series = chart.series.push(new am4charts.LineSeries());
      series.dataFields.valueY = "value";
      series.dataFields.dateX = "date";
      series.tooltipText = "Avg. Age: {value}";
      series.tooltip!.autoTextColor = false;
      // Preserve runtime behavior: original assigned the string "white";
      // amCharts coerces it at runtime, but the typed setter expects a Color.
      series.tooltip!.label.fill = "white" as unknown as am4core.Color;
      series.strokeWidth = 2;
      series.minBulletDistance = 15;
      series.tensionX = 0.8;
      series.propertyFields.strokeDasharray = "dashLength";
      series.name = "All Tickets";
      series.showOnInit = true;
      series.stroke = am4core.color("#607AFF");
      series.fill = am4core.color("#607AFF");

      const bullet = series.bullets.push(new am4charts.CircleBullet());
      bullet.circle.strokeWidth = 2;
      bullet.circle.radius = 3;

      const bulletHover = bullet.states.create("hover");
      bulletHover.properties.scale = 1.3;

      return () => {
        chart.dispose();
        legendContainer?.dispose();
      };
    }
  }, [dataExists, trendData, createChart]);

  return (
    <Chart
      chartLoading={chartLoading}
      chartName={chartName}
      chartError={dataError}
      chartData={dataExists}
      chartReload={refetchTrend}
    />
  );
};

export default AvgTicketAge;
